/* Выбор клиента или группы — общий (nav.js openClientPicker).
   Не <select>: у тренера полсотни клиентов, нужен поиск с клавиатуры. Поле в фокусе сразу,
   сверху группы, ниже клиенты по алфавиту; поиск по имени и группе;
   стрелки и Enter — выбор без мыши, Esc — закрыть. У каждого — докуда составлено.

   Использование:
     const [anchor, setAnchor] = useState<HTMLElement | null>(null);
     <button className="clbtn" onClick={e => setAnchor(e.currentTarget)}><WhoLabel who={cur} /></button>
     {anchor && <ClientPicker anchor={anchor} curId={id} onPick={(w) => …} onClose={() => setAnchor(null)} />} */
import { norm } from '@tg/domain';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { clientProgSub, groupSub, useClients, useGroups, useGroupsTill, type ClientRow, type Group } from '../lib/data';
import { grpIni, ini } from '../lib/format';
import { ICON } from './icons';
import { Cav } from './Cav';

export type Who = { kind: 'client'; id: string; name: string; sub: string; ini: string; photo?: string | null; client: ClientRow } | { kind: 'group'; id: string; name: string; sub: string; ini: string; group: Group };

export interface ClientPickerOpts {
  /** Уже выбранные — не показывать */
  exclude?: Set<string>;
  /** false — только клиенты */
  groups?: boolean;
  /** Почему выбрать нельзя: строка видна, но не выбирается */
  disabled?: (id: string) => string | null;
}

/** Все клиенты и группы с подписями — для кнопок выбора в шапках */
export function useWhoList() {
  const cs = useClients().data;
  const gs = useGroups().data;
  const till = useGroupsTill().data;
  return useMemo(() => {
    const clients: Who[] = (cs || []).filter((c) => !c.frozenAt).map((c) => ({ kind: 'client', id: c.id, name: c.name, sub: clientProgSub(c), ini: ini(c.name), photo: c.photo, client: c }));
    const groups: Who[] = (gs || []).map((g) => ({ kind: 'group', id: g.id, name: g.name, sub: groupSub(g.members.length, till?.[g.id]), ini: grpIni(g.name), group: g }));
    return { clients, groups, byId: (id: string | null | undefined) => [...groups, ...clients].find((w) => w.id === id) || null, ready: !!cs && !!gs };
  }, [cs, gs, till]);
}

/** Аватар и подпись в кнопке выбора — одна разметка на все шапки (nav.js whoBtnHTML) */
export function WhoLabel({ who }: { who: Who }) {
  return (
    <>
      <Cav ini={who.ini} photo={who.kind === 'client' ? who.photo : null} grp={who.kind === 'group'} />
      <span className="cl-t">
        <b>{who.name}</b>
        <s>{who.sub}</s>
      </span>
    </>
  );
}

/** Прокрутить список поповера так, чтобы строка id была посередине (без scrollIntoView — он двигает и страницу) */
export function centerOn(box: HTMLElement | null, id: string) {
  const list = box?.querySelector<HTMLElement>('.cl-list');
  const el = box?.querySelector<HTMLElement>(`[data-cli="${id}"]`);
  if (!list || !el) return;
  const lr = list.getBoundingClientRect();
  const er = el.getBoundingClientRect();
  list.scrollTop += er.top - lr.top - (lr.height - er.height) / 2;
}

export function ClientPicker({ anchor, curId, onPick, onClose, opts = {} }: { anchor: HTMLElement; curId?: string | null; onPick: (w: Who) => void; onClose: () => void; opts?: ClientPickerOpts }) {
  const W = useWhoList();
  const [q, setQ] = useState('');
  const [k, setK] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const inp = useRef<HTMLInputElement>(null);
  const [pos, setPos] = useState({ left: 0, top: 0 });

  useLayoutEffect(() => {
    const r = anchor.getBoundingClientRect();
    setPos({ left: r.left + window.scrollX, top: r.bottom + window.scrollY + 6 });
    /* preventScroll: в этот момент поповер ещё в (0,0) — без него страница прыгала наверх, а поповер оставался у кнопки за экраном */
    inp.current?.focus({ preventScroll: true });
  }, [anchor]);

  useEffect(() => {
    const off = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node) && !anchor.contains(e.target as Node)) onClose();
    };
    const t = setTimeout(() => document.addEventListener('click', off, true));
    return () => {
      clearTimeout(t);
      document.removeEventListener('click', off, true);
    };
  }, [anchor, onClose]);

  const qq = norm(q);
  const ex = (id: string) => !!opts.exclude?.has(id);
  const gr = opts.groups === false ? [] : W.groups.filter((g) => !ex(g.id) && (!qq || norm(g.name).includes(qq))).sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  const rows = W.clients
    .filter((w) => w.kind === 'client' && !ex(w.id))
    .filter((w) => {
      if (!qq || w.kind !== 'client') return !qq;
      const c = w.client;
      return norm(c.name).includes(qq) || (!!c.group && norm(c.group.name).includes(qq));
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  const pickable = [...gr, ...rows].filter((w) => !opts.disabled?.(w.id));
  const on = pickable[Math.min(k, pickable.length - 1)]?.id;

  /* Открыли — список сразу на текущем клиенте или группе (посередине), стрелки идут от него */
  useLayoutEffect(() => {
    if (!W.ready || !curId) return;
    const i = pickable.findIndex((w) => w.id === curId);
    if (i >= 0) setK(i);
    centerOn(box.current, curId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [W.ready]);

  const pick = (w: Who) => {
    onClose();
    onPick(w);
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!pickable.length) return;
      const n = (Math.min(k, pickable.length - 1) + (e.key === 'ArrowDown' ? 1 : -1) + pickable.length) % pickable.length;
      setK(n);
      requestAnimationFrame(() => box.current?.querySelector('.row.on')?.scrollIntoView({ block: 'nearest' }));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const w = pickable.find((x) => x.id === on) || pickable[0];
      if (w) pick(w);
    }
  };

  const row = (w: Who) => {
    const why = opts.disabled?.(w.id);
    if (why)
      return (
        <div className="row dis" key={w.id}>
          <Cav ini={w.ini} photo={w.kind === 'client' ? w.photo : null} grp={w.kind === 'group'} />
          <span className="cl-t">
            <b>{w.name}</b>
            <s>{why}</s>
          </span>
        </div>
      );
    return (
      <button key={w.id} className={'row ' + (w.id === curId ? 'cur ' : '') + (w.id === on ? 'on' : '')} data-cli={w.id} onClick={() => pick(w)}>
        <WhoLabel who={w} />
        {w.id === curId && <ICON.chk />}
      </button>
    );
  };

  return createPortal(
    <div className="sug clipick" ref={box} style={{ left: pos.left, top: pos.top }} onKeyDown={onKey} onClick={(e) => e.stopPropagation()}>
      <div className="cl-s">
        <ICON.search />
        <input
          ref={inp}
          placeholder={gr.length || (opts.groups !== false && W.groups.length) ? 'Клиент или группа' : 'Имя клиента'}
          autoComplete="off"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setK(0);
          }}
        />
      </div>
      <div className="cl-list">
        {gr.length || rows.length ? (
          <>
            {gr.length > 0 && <div className="cl-cap">Группы</div>}
            {gr.map(row)}
            {gr.length > 0 && rows.length > 0 && <div className="cl-cap">Клиенты</div>}
            {rows.map(row)}
          </>
        ) : !W.ready ? null : !W.clients.length && !W.groups.length ? (
          <div className="cap">
            Клиентов пока нет —{' '}
            <Link to="/profile" onClick={onClose}>
              пригласите первого
            </Link>
          </div>
        ) : (
          <div className="cap">Никого не нашли</div>
        )}
      </div>
    </div>,
    document.body,
  );
}
